import { ModifiedQuestion } from '@/client/quizzes/types/quizzes.type';
import { DialogContext } from '@/common/context';
import { QuizQuestionThumbnail } from '@/components/quiz/editor/QuizQuestionThumbnail';
import { DialogType } from '@/system-settings/enums';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { t } from 'i18next';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

interface QuizQuestionTimelineProps {
  questions: ModifiedQuestion[];
  currentQuestionIndex: number;
  translationKey: string;
  onSelectQuestion: (index: number) => void;
  onDeleteQuestion: (index: number) => void;
  onAddQuestion: () => void;
  onReorderQuestions: (oldIndex: number, newIndex: number) => void;
}

const TimelineCard = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  // No matching token — Figma: 48px
  padding: 48px;
  background: var(--white-main);
`;

const Heading = styled.h3`
  margin: 0 0 var(--medium-padding) 0;
  font-size: var(--heading-small-font-size);
  font-weight: 700;
  color: var(--black-main);
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: var(--small-padding);
  overflow-x: auto;
  align-items: stretch;
`;

const AddQuestionCard = styled.button.attrs({ type: 'button' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 107px;
  height: 144px;
  flex-shrink: 0;
  border: 1px dashed var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  background: var(--white-main);
  cursor: pointer;
  font-size: var(--xsmall-font-size);
  color: var(--black-main);
  gap: var(--xxsmall-padding);

  &:hover {
    background: var(--gray-lighter, var(--beige-main));
  }
`;

const AddIcon = styled.span`
  font-size: var(--medium-font-size);
  line-height: 1;
`;

export const QuizQuestionTimeline: React.FC<QuizQuestionTimelineProps> = ({
  questions,
  currentQuestionIndex,
  translationKey,
  onSelectQuestion,
  onDeleteQuestion,
  onAddQuestion,
  onReorderQuestions,
}) => {
  const { setDialogData } = useContext(DialogContext);
  const [isAnyDragging, setIsAnyDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const questionIds = questions.map((_, index) => `question-${index}`);

  const handleDragEnd = (event: DragEndEvent) => {
    setIsAnyDragging(false);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(String(active.id).replace('question-', ''), 10);
    const newIndex = parseInt(String(over.id).replace('question-', ''), 10);

    if (oldIndex !== newIndex) {
      onReorderQuestions(oldIndex, newIndex);
    }
  };

  const handleDeleteRequest = (index: number) => {
    setDialogData({
      show: true,
      type: DialogType.CONFIRMATION,
      header: t('dialog.delete'),
      message: t('content.quizEditor.deleteQuestion.confirm'),
      confirmButtonLabel: t('button.delete'),
      cancelButtonLabel: t('button.cancel'),
      onAccept: () => onDeleteQuestion(index),
    });
  };

  return (
    <TimelineCard>
      <Heading>{t('content.quizEditor.questions')}</Heading>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        sensors={sensors}
        onDragStart={() => setIsAnyDragging(true)}
        onDragCancel={() => setIsAnyDragging(false)}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questionIds}
          strategy={horizontalListSortingStrategy}
        >
          <ThumbnailRow translate="no">
            {questions.map((question, index) => (
              <QuizQuestionThumbnail
                key={`question-${index}`}
                id={`question-${index}`}
                questionIndex={index}
                questionText={question.translations[translationKey]?.text || ''}
                isSelected={index === currentQuestionIndex}
                isAnyDragging={isAnyDragging}
                onClick={onSelectQuestion}
                onDelete={handleDeleteRequest}
              />
            ))}
            <AddQuestionCard onClick={onAddQuestion}>
              <AddIcon>+</AddIcon>
              {t('content.quizEditor.addQuestion')}
            </AddQuestionCard>
          </ThumbnailRow>
        </SortableContext>
      </DndContext>
    </TimelineCard>
  );
};
